import type { FunctionComponent } from 'react'
import { useContext } from 'react'
import React from 'react'
import { useParams } from 'react-router-dom'
import { LinkButton, TagClickable, TypographyV2 as Typography } from '@which/seatbelt'
import { dynamicDatalayerPush } from '@which/shared'

import type { Filter, NeedsBasedFilter } from '../../../../../generated/frontend'
import type { PageParams } from '../../../../../routes'
import { LiveAnnouncer } from '../../../../../shared/components/LiveAnnouncer/LiveAnnouncer'
import type { AppliedFilterItem } from '../../../../../shared/types/ReviewsFilters'
import { useAppliedFilters } from '../../hooks/useAppliedFilters'
import { ReviewsTemplateContext } from '../../ReviewsTemplateContext'
import styles from './AppliedFilters.module.scss'

export const AppliedFilters: FunctionComponent<AppliedFiltersProps> = ({
  basicFilters,
  needsBasedFilters,
  removeFilterMessage,
  handleRemoveFilterMessage,
  ...rest
}) => {
  const { appliedFilters } = useAppliedFilters(basicFilters, needsBasedFilters)
  const {
    state: {
      filterContext: { filterCallback },
    },
  } = useContext(ReviewsTemplateContext)
  const { categorySlug } = useParams<PageParams>()

  const pushToDataLayer = ({ eventLabel = 'clear all filters' } = {}) => {
    dynamicDatalayerPush({
      eventAction: 'clear filters',
      eventCategory: 'refine results',
      eventLabel,
    })
  }

  const Header = () => (
    <div className={styles.appliedFiltersHeader}>
      <Typography
        tag="h3"
        textStyle="sb-text-body-default-strong"
        className={styles.appliedFiltersHeaderTitle}
      >
        Applied filters
      </Typography>
      <LinkButton
        onClick={() => {
          filterCallback({ reset: true })
          pushToDataLayer()
        }}
        data-testid="clear-filters"
        aria-controls="ea-search-results"
      >
        <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
          Clear all filters
        </Typography>
      </LinkButton>
    </div>
  )

  const ClickableTags = () => (
    <>
      {appliedFilters?.map(
        (
          { code, path, label, value, filterLabel, questionText, nbfLabel }: AppliedFilterItem,
          index
        ) => {
          return (
            <TagClickable
              aria-label={`Remove ${label} filter`}
              className={styles.appliedFiltersFilter}
              text={label}
              withIcon={true}
              key={`${code}-${index}`}
              clickHandler={() => {
                handleRemoveFilterMessage(`${label} removed`)
                filterCallback({ path, filterValue: value, questionText, nbfLabel })
                pushToDataLayer({ eventLabel: `${categorySlug} - ${filterLabel} - ${label}` })
              }}
            />
          )
        }
      )}
    </>
  )

  return (
    (!!appliedFilters?.length || null) && (
      <section className={styles.appliedFilters} {...rest}>
        <Header />
        <ClickableTags />
        <LiveAnnouncer liveText={removeFilterMessage} />
      </section>
    )
  )
}

export type AppliedFiltersProps = {
  basicFilters: Filter[]
  needsBasedFilters: NeedsBasedFilter[]
  removeFilterMessage: string
  handleRemoveFilterMessage: (message: string) => void
}
